import { format } from "date-fns";

export const DATE_FORMAT = {
  MMM_YY: "MMM yy",
  SHORT: "d MMM yy",
  dMMMyyyy: "d MMM yyyy",
  LONG: "dd MMM yyyy HH:mm:ss",
  dMMM: "d MMM",
  MMMd: "MMM d",
};

export const formatDate = (
  date: string,
  formatString: string = DATE_FORMAT.SHORT
) => {
  try {
    if (!date) return "";
    const parsedDate = new Date(date);
    // Check if date is valid
    if (isNaN(parsedDate.getTime())) {
      return "";
    }
    return format(parsedDate, formatString);
  } catch (error) {
    console.error("Error formatting date:", error);
    return "";
  }
};

/**
 * Converts a Date object to the end of that day in UTC (23:59:59.999Z)
 * Preserves the local date that the user selected, regardless of timezone
 * 
 * Example: If user selects December 5, 2025 in GMT+2, this returns "2025-12-05T23:59:59.999Z"
 * (not "2025-12-04T23:59:59.999Z" which would be the UTC date)
 *
 * @param date - Date object (e.g., user-selected date in their local timezone)
 * @returns ISO date string at end of day in UTC (e.g., "2025-12-05T23:59:59.999Z")
 */
export const getEndOfDayISOString = (
  date: Date | undefined
): string | undefined => {
  if (!date) return undefined;

  try {
    if (isNaN(date.getTime())) return undefined;

    // Extract year, month, and day from the LOCAL date (not UTC)
    // This preserves the date the user actually selected, regardless of timezone
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();

    // Create a new date in UTC with the same year, month, day, but at end of day
    // This ensures the backend receives the end of the selected day in UTC
    const endOfDay = new Date(Date.UTC(year, month, day, 23, 59, 59, 999));

    return endOfDay.toISOString();
  } catch (error) {
    console.error("Error converting date to end of day:", error);
    return undefined;
  }
};

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 B";
  const k = 1000;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const value = bytes / Math.pow(k, i);
  // Round KB to whole number, MB to 1 decimal, others to 2 decimals
  const formattedValue =
    i === 1
      ? Math.round(value)
      : i === 2
      ? parseFloat(value.toFixed(1))
      : parseFloat(value.toFixed(2));
  return `${formattedValue} ${sizes[i]}`;
};

export const getMonthString = (input: string | number): string => {
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth();
  // const currentYear = currentDate.getFullYear();

  const offset = typeof input === "string" ? parseInt(input, 10) : input;
  if (isNaN(offset)) {
    throw new Error(
      "Invalid input: must be a number or a string representing a number"
    );
  }

  let targetMonth = currentMonth + offset;
  // let targetYear = currentYear;

  while (targetMonth < 0) {
    targetMonth += 12;
    // targetYear -= 1;
  }

  while (targetMonth > 11) {
    targetMonth -= 12;
    // targetYear += 1;
  }

  return monthNames[targetMonth];
};

export enum Environment {
  LOCAL = "local",
  DEV = "development",
  PRODUCTION = "production",
}

export const getEnvironment = () => {
  const url = window.location.href;
  const isProduction = !(
    url.includes("localhost") ||
    url.includes("creditit-rose.vercel.app") ||
    url.includes("app-dev.creditit.ai/")
  );

  const isLocal = url.includes("localhost");

  return isLocal
    ? Environment.LOCAL
    : isProduction
    ? Environment.PRODUCTION
    : Environment.DEV;
};

export const anonymizeString = (
  str: string,
  numberOfCharacters: number = 4
) => {
  return (
    str.substring(0, numberOfCharacters) + "*".repeat(4) + " " + "*".repeat(4)
  );
};

export const formatAmount = (amount: number): string => {
  return Math.round(amount).toLocaleString("en-US");
};

export const formatTimeAgo = (dateString: string): string => {
  try {
    if (!dateString) return "";

    // If no timezone info at all, treat as UTC
    const normalized = /Z$|[+-]\d{2}:\d{2}$/.test(dateString)
      ? dateString
      : dateString + "Z";
    const serverDate = new Date(normalized);

    if (isNaN(serverDate.getTime())) return "";

    // Get current date in local timezone
    const now = new Date();

    // Convert both dates to UTC timestamps for comparison
    const serverTimestamp = serverDate.getTime();
    const nowTimestamp = now.getTime();

    // Calculate differences using timestamps (which are always in UTC)
    const minutesDiff = Math.floor(
      (nowTimestamp - serverTimestamp) / (1000 * 60)
    );
    const hoursDiff = Math.floor(
      (nowTimestamp - serverTimestamp) / (1000 * 60 * 60)
    );
    const daysDiff = Math.floor(
      (nowTimestamp - serverTimestamp) / (1000 * 60 * 60 * 24)
    );

    if (minutesDiff < 1) {
      return "less than a minute ago";
    } else if (minutesDiff < 60) {
      return `${minutesDiff} ${minutesDiff === 1 ? "minute" : "minutes"} ago`;
    } else if (hoursDiff < 24) {
      return `${hoursDiff} ${hoursDiff === 1 ? "hour" : "hours"} ago`;
    } else {
      return `${daysDiff} ${daysDiff === 1 ? "day" : "days"} ago`;
    }
  } catch (error) {
    console.error("Error formatting time ago:", error);
    return "";
  }
};

export const formatWeekRange = (dateString: string): string => {
  try {
    if (!dateString) return "";

    // Parse the date string
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";

    // Get the day of the week (0 = Sunday, 1 = Monday, etc.)
    const dayOfWeek = date.getDay();

    // Calculate the start of the week (Monday)
    // If it's Sunday (0), we want to go back 6 days to get to Monday
    // If it's Monday (1), we want to go back 0 days
    // If it's Tuesday (2), we want to go back 1 day, etc.
    const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

    // Calculate start and end of the week
    const startOfWeek = new Date(date);
    startOfWeek.setDate(date.getDate() - daysToMonday);

    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);

    // Format the dates
    const startFormatted = format(startOfWeek, "d MMM");
    const endFormatted = format(endOfWeek, "d MMM");

    return `${startFormatted} - ${endFormatted}`;
  } catch (error) {
    console.error("Error formatting week range:", error);
    return "";
  }
};

interface ICalculateDaysDiffOptions {
  switchToMonthsAfterDays?: number;
}

export const calculateDaysDiff = (
  date: string | Date,
  options: ICalculateDaysDiffOptions = {}
): string => {
  try {
    const { switchToMonthsAfterDays = 90 } = options;

    // Parse the input date
    const targetDate = typeof date === "string" ? new Date(date) : date;
    if (isNaN(targetDate.getTime())) return "";

    const now = new Date();

    // Set both dates to UTC midnight for accurate day calculation
    const targetUtc = Date.UTC(
      targetDate.getFullYear(),
      targetDate.getMonth(),
      targetDate.getDate()
    );
    const nowUtc = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());

    const diffMs = targetUtc - nowUtc;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    // Calculate months and years based on the target and current dates
    let months = (targetDate.getFullYear() - now.getFullYear()) * 12;
    months += targetDate.getMonth() - now.getMonth();
    // Adjust for day of month
    if (targetDate.getDate() < now.getDate()) {
      months--;
    }

    const diffYears = Math.floor(Math.abs(months) / 12);
    const isPast = months < 0;

    // If the date is today
    if (Math.abs(diffDays) === 0) {
      return "Today";
    }

    // Helper function to format the output
    const formatOutput = (value: number, unit: string, isPast: boolean) => {
      const absValue = Math.abs(value);
      const pluralUnit = absValue === 1 ? unit : unit + "s";
      return isPast
        ? `${absValue} ${pluralUnit} ago`
        : `In ${absValue} ${pluralUnit}`;
    };

    // Past dates
    if (isPast) {
      if (diffYears > 0) {
        return formatOutput(diffYears, "year", true);
      }
      if (Math.abs(diffDays) > switchToMonthsAfterDays) {
        return formatOutput(Math.abs(months), "month", true);
      }
      return formatOutput(Math.abs(diffDays), "day", true);
    }

    // Future dates
    if (diffYears > 0) {
      return formatOutput(diffYears, "year", false);
    }
    if (diffDays > switchToMonthsAfterDays) {
      return formatOutput(months, "month", false);
    }
    return formatOutput(diffDays, "day", false);
  } catch (error) {
    console.error("Error calculating days difference:", error);
    return "";
  }
};
